import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ArrowRight,
  MapPin,
  Briefcase,
  Users,
  Eye,
  FileText,
  TrendingUp,
  Globe,
  Building2,
  Calendar,
  FolderOpen,
  Plus,
  Image,
  Type,
} from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

const formatNumber = (n) => n.toLocaleString("fr-BE");

const CompanyPage = ({
  spontaneousApplicationsYearCount = 0,
}) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const intl = useIntl();
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      id: 1,
      icon: Building2,
      label: intl.formatMessage({ id: "landing_recruiter.company_page.step1_label" }),
      desc: intl.formatMessage({ id: "landing_recruiter.company_page.step1_desc" }),
    },
    {
      id: 2,
      icon: Image,
      label: intl.formatMessage({ id: "landing_recruiter.company_page.step2_label" }),
      desc: intl.formatMessage({ id: "landing_recruiter.company_page.step2_desc" }),
    },
    {
      id: 3,
      icon: Type,
      label: intl.formatMessage({ id: "landing_recruiter.company_page.step3_label" }),
      desc: intl.formatMessage({ id: "landing_recruiter.company_page.step3_desc" }),
    },
    {
      id: 4,
      icon: Briefcase,
      label: intl.formatMessage({ id: "landing_recruiter.company_page.step4_label" }),
      desc: intl.formatMessage({ id: "landing_recruiter.company_page.step4_desc" }),
    },
  ];

  const features = [
    {
      icon: Eye,
      stat: "4 500",
      label: intl.formatMessage({ id: "landing_recruiter.company_page.feature_views" }),
      desc: intl.formatMessage({ id: "landing_recruiter.company_page.feature_views_desc" }),
    },
    {
      icon: FileText,
      stat: `+${formatNumber(spontaneousApplicationsYearCount)}`,
      label: intl.formatMessage({ id: "landing_recruiter.company_page.feature_applications" }),
      desc: intl.formatMessage({ id: "landing_recruiter.company_page.feature_applications_desc" }),
    },
    {
      icon: TrendingUp,
      stat: "SEO",
      label: intl.formatMessage({ id: "landing_recruiter.company_page.feature_seo" }),
      desc: intl.formatMessage({ id: "landing_recruiter.company_page.feature_seo_desc" }),
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "32px", md: "48px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(178, 93%, 10%)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ maxWidth: "1152px", mx: "auto" }}>
        {/* Heading */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "24px" }}
        >
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem" },
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              color: "hsl(0, 0%, 98%)",
              letterSpacing: "-0.025em",
              mb: "16px",
              mt: 0,
            }}
          >
            <FormattedMessage id="landing_recruiter.company_page.title" />
          </Box>
          <Box
            component="p"
            sx={{
              color: "hsla(0, 0%, 98%, 0.6)",
              fontSize: "1.125rem",
              maxWidth: "672px",
              mx: "auto",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              textAlign: "center",
            }}
          >
            <FormattedMessage id="landing_recruiter.company_page.subtitle" />
          </Box>
        </Box>

        {/* Badge */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          sx={{ display: "flex", justifyContent: "center", mb: "40px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              px: "20px",
              py: "8px",
              borderRadius: "9999px",
              bgcolor: "hsla(145, 63%, 42%, 0.15)",
              border: "1px solid hsla(145, 63%, 42%, 0.25)",
              color: "hsl(145, 63%, 42%)",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            }}
          >
            <Check size={16} /> <FormattedMessage id="landing_recruiter.company_page.badge" />
          </Box>
        </Box>

        {/* Interactive demo layout */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "300px 1fr" },
            gap: "24px",
            mb: "48px",
          }}
        >
          {/* Left: Step selector */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isActive = activeStep === step.id;
              const isCompleted = activeStep > step.id;
              return (
                <Box
                  component={motion.button}
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  onClick={() => setActiveStep(step.id)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    p: "16px",
                    borderRadius: "0.75rem",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    ...(isActive
                      ? {
                        bgcolor: "hsla(145, 63%, 42%, 0.15)",
                        border: "1px solid hsla(145, 63%, 42%, 0.3)",
                        boxShadow:
                            "0 10px 15px -3px hsla(145, 63%, 42%, 0.05)",
                      }
                      : {
                        bgcolor: "hsla(0, 0%, 98%, 0.03)",
                        border: "1px solid hsla(0, 0%, 98%, 0.08)",
                        "&:hover": {
                          borderColor: "hsla(0, 0%, 98%, 0.15)",
                        },
                      }),
                  }}
                >
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.3s ease",
                      ...(isCompleted
                        ? {
                          bgcolor: "hsla(145, 63%, 42%, 0.2)",
                        }
                        : isActive
                          ? {
                            bgcolor: "hsl(145, 63%, 42%)",
                          }
                          : {
                            bgcolor: "hsla(0, 0%, 98%, 0.06)",
                          }),
                    }}
                  >
                    {isCompleted ? (
                      <Check size={16} color="hsl(145, 63%, 42%)" />
                    ) : (
                      <Icon
                        size={16}
                        color={
                          isActive
                            ? "hsl(178, 93%, 10%)"
                            : "hsla(0, 0%, 98%, 0.3)"
                        }
                      />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      component="p"
                      sx={{
                        fontSize: "0.875rem",
                        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                        transition: "color 0.3s ease",
                        color: isActive
                          ? "hsl(145, 63%, 42%)"
                          : "hsla(0, 0%, 98%, 0.7)",
                        m: 0,
                      }}
                    >
                      {step.label}
                    </Box>
                    <Box
                      component="p"
                      sx={{
                        fontSize: "11px",
                        color: "hsla(0, 0%, 98%, 0.4)",
                        m: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      {step.desc}
                    </Box>
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      ml: "auto",
                      fontSize: "10px",
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      flexShrink: 0,
                      color: isActive
                        ? "hsl(145, 63%, 42%)"
                        : "hsla(0, 0%, 98%, 0.2)",
                    }}
                  >
                    {step.id}/4
                  </Box>
                </Box>
              );
            })}

            {/* Progress bar */}
            <Box sx={{ pt: "8px", px: "4px" }}>
              <Box
                sx={{
                  height: "6px",
                  borderRadius: "9999px",
                  bgcolor: "hsla(0, 0%, 98%, 0.06)",
                  overflow: "hidden",
                }}
              >
                <Box
                  component={motion.div}
                  animate={{ width: `${(activeStep / 4) * 100}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  sx={{
                    height: "100%",
                    borderRadius: "9999px",
                    bgcolor: "hsl(145, 63%, 42%)",
                  }}
                />
              </Box>
              <Box
                component="p"
                sx={{
                  fontSize: "10px",
                  color: "hsla(0, 0%, 98%, 0.3)",
                  textAlign: "center",
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                  mt: "8px",
                }}
              >
                {intl.formatMessage({ id: "landing_recruiter.company_page.step_progress" }, { step: activeStep })}
              </Box>
            </Box>
          </Box>

          {/* Right: Live preview mockup */}
          <Box sx={{ position: "relative" }}>
            {/* Glow */}
            <Box
              sx={{
                position: "absolute",
                inset: "-16px",
                bgcolor: "hsla(145, 63%, 42%, 0.04)",
                borderRadius: "1.5rem",
                filter: "blur(24px)",
                pointerEvents: "none",
              }}
            />

            <Box
              sx={{
                position: "relative",
                borderRadius: "1rem",
                border: "1px solid hsla(0, 0%, 98%, 0.1)",
                bgcolor: "hsla(0, 0%, 98%, 0.04)",
                backdropFilter: "blur(8px)",
                overflow: "hidden",
                boxShadow: "0 25px 50px -12px hsla(0, 0%, 0%, 0.2)",
              }}
            >
              {/* Browser chrome */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  px: "16px",
                  py: "12px",
                  borderBottom: "1px solid hsla(0, 0%, 98%, 0.08)",
                  bgcolor: "hsla(0, 0%, 98%, 0.03)",
                }}
              >
                <Box sx={{ display: "flex", gap: "6px" }}>
                  <Box
                    sx={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "9999px",
                      bgcolor: "hsla(0, 0%, 98%, 0.15)",
                    }}
                  />
                  <Box
                    sx={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "9999px",
                      bgcolor: "hsla(0, 0%, 98%, 0.15)",
                    }}
                  />
                  <Box
                    sx={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "9999px",
                      bgcolor: "hsla(0, 0%, 98%, 0.15)",
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, mx: "16px" }}>
                  <Box
                    sx={{
                      bgcolor: "hsla(0, 0%, 98%, 0.06)",
                      borderRadius: "6px",
                      px: "12px",
                      py: "4px",
                      fontSize: "10px",
                      color: "hsla(0, 0%, 98%, 0.3)",
                      fontFamily: "monospace",
                    }}
                  >
                    squarehub.eu/entreprise/votre-entreprise
                  </Box>
                </Box>
              </Box>

              {/* Mockup content that builds up */}
              <Box sx={{ minHeight: "380px", position: "relative" }}>
                {/* Step 2: Cover banner */}
                <Box
                  sx={{
                    position: "relative",
                    height: "112px",
                    transition: "all 0.5s ease",
                    ...(activeStep >= 2
                      ? {
                        background:
                            "linear-gradient(to right, hsla(145, 63%, 42%, 0.4), hsla(145, 63%, 42%, 0.25), hsla(145, 63%, 42%, 0.1))",
                      }
                      : {
                        bgcolor: "hsla(0, 0%, 98%, 0.04)",
                      }),
                  }}
                >
                  {activeStep >= 2 && (
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(to bottom right, hsla(145, 63%, 42%, 0.3), hsla(145, 63%, 42%, 0.15), transparent)",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: "12px",
                          left: "16px",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "9px",
                            px: "8px",
                            py: "4px",
                            borderRadius: "6px",
                            bgcolor: "hsla(178, 93%, 10%, 0.6)",
                            backdropFilter: "blur(4px)",
                            color: "hsla(0, 0%, 98%, 0.7)",
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          ← {intl.formatMessage({ id: "landing_recruiter.company_page.back" })}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {activeStep < 2 && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "10px",
                          color: "hsla(0, 0%, 98%, 0.2)",
                          fontFamily:
                            "Segoe UI Regular, system-ui, sans-serif",
                        }}
                      >
                        <Image size={12} />
                        {intl.formatMessage({ id: "landing_recruiter.company_page.cover_photo" })}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Step 1: Company info header */}
                <AnimatePresence>
                  {activeStep >= 1 && (
                    <Box
                      component={motion.div}
                      key="info"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      sx={{ px: { xs: "20px", md: "32px" }, position: "relative" }}
                    >
                      {/* Logo overlapping banner */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: "16px",
                          mt: "-32px",
                        }}
                      >
                        <Box
                          sx={{
                            width: "64px",
                            height: "64px",
                            borderRadius: "0.75rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            boxShadow:
                              "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
                            transition: "all 0.5s ease",
                            ...(activeStep >= 2
                              ? {
                                bgcolor: "hsl(178, 93%, 10%)",
                                border:
                                    "2px solid hsla(0, 0%, 98%, 0.1)",
                              }
                              : {
                                bgcolor: "hsla(0, 0%, 98%, 0.06)",
                                border:
                                    "2px dashed hsla(0, 0%, 98%, 0.15)",
                              }),
                          }}
                        >
                          {activeStep >= 2 ? (
                            <Building2
                              size={24}
                              color="hsl(145, 63%, 42%)"
                            />
                          ) : (
                            <Plus
                              size={14}
                              color="hsla(0, 0%, 98%, 0.25)"
                            />
                          )}
                        </Box>
                        <Box sx={{ pb: "4px" }}>
                          <Box
                            component={motion.p}
                            key={`name-${activeStep}`}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            sx={{
                              fontSize: "1.125rem",
                              fontFamily:
                                "Segoe UI Black, system-ui, sans-serif",
                              color: "hsla(0, 0%, 98%, 0.9)",
                              m: 0,
                            }}
                          >
                            Archivault
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontSize: "11px",
                              color: "hsla(0, 0%, 98%, 0.4)",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <Briefcase size={9} /> Architecture
                            </Box>
                            <Box
                              component="span"
                              sx={{ color: "hsla(0, 0%, 98%, 0.2)" }}
                            >
                              ·
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <MapPin size={9} />{" "}
                              {intl.formatMessage({ id: "landing_recruiter.company_page.demo_company_location" })}
                            </Box>
                            {activeStep >= 3 && (
                              <>
                                <Box
                                  component="span"
                                  sx={{
                                    color: "hsla(0, 0%, 98%, 0.2)",
                                  }}
                                >
                                  ·
                                </Box>
                                <Box
                                  component={motion.span}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    color: "hsla(145, 63%, 42%, 0.7)",
                                  }}
                                >
                                  <Globe size={9} /> {intl.formatMessage({ id: "landing_recruiter.company_page.see_website" })}
                                </Box>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Tab navigation */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "20px",
                          mt: "16px",
                          borderBottom:
                            "1px solid hsla(0, 0%, 98%, 0.08)",
                          pb: 0,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "11px",
                            fontFamily:
                              "Segoe UI Bold, system-ui, sans-serif",
                            color: "hsla(0, 0%, 98%, 0.8)",
                            pb: "8px",
                            borderBottom:
                              "2px solid hsl(145, 63%, 42%)",
                          }}
                        >
                          {intl.formatMessage({ id: "landing_recruiter.company_page.profile_tab" })}
                        </Box>
                        {activeStep >= 3 && (
                          <Box
                            component={motion.span}
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            sx={{
                              fontSize: "11px",
                              color: "hsla(0, 0%, 98%, 0.35)",
                              pb: "8px",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.company_page.story_tab" })}
                          </Box>
                        )}
                        {activeStep >= 3 && (
                          <Box
                            component={motion.span}
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.05 }}
                            sx={{
                              fontSize: "11px",
                              color: "hsla(0, 0%, 98%, 0.35)",
                              pb: "8px",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.company_page.projects_tab" })}
                          </Box>
                        )}
                        {activeStep >= 4 && (
                          <Box
                            component={motion.span}
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            sx={{
                              fontSize: "11px",
                              color: "hsla(0, 0%, 98%, 0.35)",
                              pb: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontFamily:
                                "Segoe UI Regular, system-ui, sans-serif",
                            }}
                          >
                            Jobs
                            <Box
                              component="span"
                              sx={{
                                fontSize: "8px",
                                px: "6px",
                                py: "2px",
                                borderRadius: "9999px",
                                bgcolor: "hsla(145, 63%, 42%, 0.2)",
                                color: "hsl(145, 63%, 42%)",
                                fontFamily:
                                  "Segoe UI Bold, system-ui, sans-serif",
                              }}
                            >
                              5
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </AnimatePresence>

                {/* Step 3: Description & stats */}
                <AnimatePresence>
                  {activeStep >= 3 && (
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      transition={{ duration: 0.4 }}
                      sx={{ px: { xs: "20px", md: "32px" }, mt: "16px" }}
                    >
                      <Box
                        component="p"
                        sx={{
                          fontSize: "12px",
                          fontFamily:
                            "Segoe UI Bold, system-ui, sans-serif",
                          color: "hsla(0, 0%, 98%, 0.7)",
                          mb: "10px",
                          mt: 0,
                        }}
                      >
                        {intl.formatMessage({ id: "landing_recruiter.company_page.about_company" }, { company: "Archivault" })}
                      </Box>

                      {/* Stat pills */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          mb: "12px",
                        }}
                      >
                        {[
                          { icon: Calendar, label: "2017" },
                          { icon: Users, label: "200 +" },
                          { icon: FolderOpen, label: intl.formatMessage({ id: "landing_recruiter.company_page.projects_count" }) },
                        ].map((pill, i) => {
                          const PillIcon = pill.icon;
                          return (
                            <Box
                              component={motion.span}
                              key={i}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: i * 0.08 }}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                px: "12px",
                                py: "6px",
                                borderRadius: "9999px",
                                bgcolor: "hsla(0, 0%, 98%, 0.05)",
                                border:
                                  "1px solid hsla(0, 0%, 98%, 0.08)",
                                fontSize: "10px",
                                fontFamily:
                                  "Segoe UI Regular, system-ui, sans-serif",
                                color: "hsla(0, 0%, 98%, 0.5)",
                              }}
                            >
                              <PillIcon
                                size={10}
                                color="hsla(145, 63%, 42%, 0.7)"
                              />
                              {pill.label}
                            </Box>
                          );
                        })}
                      </Box>

                      {/* Description lines */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <Box
                          component={motion.div}
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 0.6, delay: 0.1 }}
                          sx={{
                            height: "8px",
                            borderRadius: "9999px",
                            bgcolor: "hsla(0, 0%, 98%, 0.06)",
                          }}
                        />
                        <Box
                          component={motion.div}
                          initial={{ width: 0 }}
                          animate={{ width: "92%" }}
                          transition={{ duration: 0.6, delay: 0.2 }}
                          sx={{
                            height: "8px",
                            borderRadius: "9999px",
                            bgcolor: "hsla(0, 0%, 98%, 0.06)",
                          }}
                        />
                        <Box
                          component={motion.div}
                          initial={{ width: 0 }}
                          animate={{ width: "78%" }}
                          transition={{ duration: 0.6, delay: 0.3 }}
                          sx={{
                            height: "8px",
                            borderRadius: "9999px",
                            bgcolor: "hsla(0, 0%, 98%, 0.06)",
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </AnimatePresence>

                {/* Step 4: Job listings */}
                <AnimatePresence>
                  {activeStep >= 4 && (
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      transition={{ duration: 0.4 }}
                      sx={{
                        mx: { xs: "20px", md: "32px" },
                        mt: "16px",
                        mb: "24px",
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: "0.75rem",
                          border: "1px solid hsla(0, 0%, 98%, 0.08)",
                          bgcolor: "hsla(0, 0%, 98%, 0.03)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            px: "16px",
                            py: "10px",
                            borderBottom:
                              "1px solid hsla(0, 0%, 98%, 0.06)",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Briefcase
                            size={10}
                            color="hsl(145, 63%, 42%)"
                          />
                          <Box
                            component="p"
                            sx={{
                              fontSize: "11px",
                              fontFamily:
                                "Segoe UI Bold, system-ui, sans-serif",
                              color: "hsla(0, 0%, 98%, 0.6)",
                              m: 0,
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.company_page.offers_at_company" }, { count: 5, company: "Archivault" })}
                          </Box>
                        </Box>
                        {[
                          {
                            title: intl.formatMessage({ id: "landing_recruiter.company_page.job1_title" }),
                            type: intl.formatMessage({ id: "landing_recruiter.company_page.job_type_permanent" }),
                          },
                          {
                            title: intl.formatMessage({ id: "landing_recruiter.company_page.job2_title" }),
                            type: intl.formatMessage({ id: "landing_recruiter.company_page.job_type_permanent" }),
                          },
                        ].map((job, i) => (
                          <Box
                            component={motion.div}
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              delay: 0.15 + i * 0.07,
                            }}
                            sx={{
                              px: "16px",
                              py: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderBottom:
                                "1px solid hsla(0, 0%, 98%, 0.04)",
                            }}
                          >
                            <Box>
                              <Box
                                component="p"
                                sx={{
                                  fontSize: "11px",
                                  fontFamily:
                                    "Segoe UI Regular, system-ui, sans-serif",
                                  color: "hsla(0, 0%, 98%, 0.7)",
                                  m: 0,
                                }}
                              >
                                {job.title}
                              </Box>
                              <Box
                                component="p"
                                sx={{
                                  fontSize: "9px",
                                  color: "hsla(0, 0%, 98%, 0.3)",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontFamily:
                                    "Segoe UI Regular, system-ui, sans-serif",
                                  mt: "2px",
                                }}
                              >
                                <MapPin size={8} />{" "}
                                {intl.formatMessage({ id: "landing_recruiter.company_page.demo_company_location" })}
                              </Box>
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                fontSize: "8px",
                                px: "8px",
                                py: "2px",
                                borderRadius: "9999px",
                                fontFamily:
                                  "Segoe UI Bold, system-ui, sans-serif",
                                bgcolor:
                                  "hsla(145, 63%, 42%, 0.15)",
                                color: "hsl(145, 63%, 42%)",
                              }}
                            >
                              {job.type}
                            </Box>
                          </Box>
                        ))}
                        <Box
                          component={motion.div}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.35 }}
                          sx={{
                            px: "16px",
                            py: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontSize: "10px",
                              fontFamily:
                                "Segoe UI Bold, system-ui, sans-serif",
                              color: "hsla(145, 63%, 42%, 0.8)",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              cursor: "pointer",
                              transition: "color 0.2s ease",
                              "&:hover": {
                                color: "hsl(145, 63%, 42%)",
                              },
                            }}
                          >
                            {intl.formatMessage({ id: "landing_recruiter.company_page.see_more_offers" }, { count: 3 })}{" "}
                            <ArrowRight size={10} />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </AnimatePresence>

                {activeStep < 4 && <Box sx={{ height: "32px" }} />}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Feature cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, 1fr)",
            },
            gap: "16px",
            mb: "40px",
          }}
        >
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Box
                component={motion.div}
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                sx={{
                  borderRadius: "1rem",
                  border: "1px solid hsla(0, 0%, 98%, 0.1)",
                  bgcolor: "hsla(0, 0%, 98%, 0.04)",
                  backdropFilter: "blur(8px)",
                  p: "20px",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "0.75rem",
                    bgcolor: "hsla(145, 63%, 42%, 0.15)",
                    border: "1px solid hsla(145, 63%, 42%, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: "12px",
                  }}
                >
                  <Icon size={18} color="hsl(145, 63%, 42%)" />
                </Box>
                <Box
                  component="p"
                  sx={{
                    fontSize: "1.25rem",
                    fontFamily:
                      "Segoe UI Black, system-ui, sans-serif",
                    color: "hsl(145, 63%, 42%)",
                    mb: 0,
                    mt: "4px",
                  }}
                >
                  {f.stat}
                </Box>
                <Box
                  component="p"
                  sx={{
                    fontSize: "0.8rem",
                    fontFamily:
                      "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsla(0, 0%, 98%, 0.8)",
                    mb: "4px",
                    mt: 0,
                  }}
                >
                  {f.label}
                </Box>
                <Box
                  component="p"
                  sx={{
                    fontSize: "0.75rem",
                    color: "hsla(0, 0%, 98%, 0.45)",
                    m: 0,
                    fontFamily:
                      "Segoe UI Regular, system-ui, sans-serif",
                  }}
                >
                  {f.desc}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* CTA */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center" }}
        >
          <Button
            component="a"
            href={`/${i18nLocale}/recruiters/sign_up`}
            sx={{
              bgcolor: "hsl(153 68% 25%)",
              color: "white",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              fontWeight: 700,
              fontSize: "0.875rem",
              px: "1.5rem",
              py: "0.875rem",
              borderRadius: "0.75rem",
              textTransform: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              textDecoration: "none",
              "&:hover": {
                bgcolor: "hsl(153 68% 25% / 0.9)",
                textDecoration: "none",
              },
            }}
          >
            <FormattedMessage id="landing_recruiter.company_page.cta" /> <ArrowRight size={14} className="arrow-icon" />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyPage;
